import React, { useState, useEffect, useRef,useCallback,useMemo } from "react";
import 'pepjs'

import { useAuth } from "../../../app/context";

// import {ReactComponent as NextIcon} from '../../images/icons/next.svg';
import {ReactComponent as PreviousIcon} from '../../../assets/images/previous.svg';

import "./styles.scss";



const DotsComponent = React.memo(({activeIndex=0,count=0,onClick=()=>{}})=>{
  const _index = count > 0 ? activeIndex % count : 0;
  const handleClick = useMemo(() => {
    return (index) => onClick(index);
  }, [onClick]);

  return(<div className="cardslidercontainer__dots">
    {new Array(count).fill(undefined).map((_, index) => {
      return(<button
      key={index}
      className={`dot ${_index === index ? "active" : ""}`}
      onClick={() => handleClick(index)}
      ></button>)
    })}
  </div>)
},
(prevProps, nextProps) => {
   return (
     prevProps.activeIndex === nextProps.activeIndex &&
     prevProps.count === nextProps.count &&
     prevProps.onClick === nextProps.onClick
    );
})


const Carousel = React.memo(({
  children,
  dragable = true,
  dots = true,
  autoplay = true,
  autoplayHoverPause = true,
  autoplayTimeout = 3000,
  smartSpeed = 500,
  responsive = {},
}) => {

  const { breakpoint } = useAuth();

  let totalItems = React.Children.count(children);
  const [settings, setSettings] = useState({items:1,margin:0});

  const currentRef = useRef(0);
  const trackRef  = useRef(null);
  const [currentIndex , setCurrentIndex ] = useState(0);

  const slideWidth = useRef(0);

  const autoplayTimer = useRef(null);
  const sliderRef = useRef(null);
  const observerRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);


  const responsiveSettings = () => {
    let width = window.innerWidth;
    let matchedBreakpoint = Object.keys(responsive).sort((a, b) => b - a).find((bp) => width >= bp);
    let {items,margin} = responsive[matchedBreakpoint]??{};
    
    items = items || 1;
    margin = margin || 0;
    setSettings((prev)=>({...prev,items,margin}));
  };

  useEffect(() => {
    trackRef.current.style.setProperty("--smart-speed", `${smartSpeed}ms`);
  }, [smartSpeed]);

  useEffect(() => {
    handleGoTo(0);
    responsiveSettings();
  }, [children]);

  useEffect(()=>{    
    responsiveSettings();
  },[breakpoint]);

  function handleGoTo(i) {
    let _index = i;
    let _speed = smartSpeed;    
    
    if(i < 0){
      trackRef.current.style.setProperty("--smart-speed","0ms");
      trackRef.current.style.setProperty("--current-index", totalItems);
      trackRef.current.getBoundingClientRect();
      trackRef.current.style.setProperty("--smart-speed", `${_speed}ms`);

      _index = totalItems - 1;
    }else if(i > totalItems){
      trackRef.current.style.setProperty("--smart-speed","0ms");
      trackRef.current.style.setProperty("--current-index", 0);
      trackRef.current.getBoundingClientRect();
      trackRef.current.style.setProperty("--smart-speed", `${_speed}ms`);
      _index = 1;
    }

    currentRef.current = _index;
    trackRef.current.style.setProperty("--current-index", _index);
    setCurrentIndex(_index);
  }

  const handlePrev = () => {
    handleGoTo(currentRef.current - 1);
  };

  const handleNext = () => {
    handleGoTo(currentRef.current + 1);
  };

  
  // ------------------ AUTOPLAY LOGIC ------------------ //
// autoplayHoverPause
  const stopAutoplay = useCallback(() => {    
    if (autoplayTimer.current) {
      clearInterval(autoplayTimer.current);
      autoplayTimer.current = null;
    }
  }, []);

  const startAutoplay = useCallback(() => {
    stopAutoplay(); // avoid duplicate timers
    autoplayTimer.current = setInterval(() => {
      handleNext();
    }, autoplayTimeout);
  }, [autoplayTimeout]);

  useEffect(() => {
    if(!autoplay) {
      stopAutoplay();
      if(observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    const el = sliderRef.current;

    const observer = new IntersectionObserver(
      (entries) => {        
        if(entries[0].isIntersecting) startAutoplay();
        else stopAutoplay();
      },
      {
        threshold: 0.5, // slider must be 50% visible
      }
    );

    observer.observe(el);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [autoplay, startAutoplay, stopAutoplay]);


  const handleDragStart = (e) => {
    e?.preventDefault();
    if (!dragable) return;
    e.target.setPointerCapture(e.pointerId);

    isDragging.current = true;
    let clientX = e?.clientX??0;
    startX.current = clientX;
    
    stopAutoplay();
  };


  const handleDragEnd = (e) => {
    e?.preventDefault();
    if (!isDragging.current) return;
    isDragging.current = false;
    e.target.releasePointerCapture(e.pointerId);

    let clientX = e?.clientX??0;
    let deltaX = clientX - startX.current;
    startX.current = 0;

    if (deltaX < -50){
      handleNext();
    } else if (deltaX > 50){
      handlePrev();
    }

    if (observerRef.current && !autoplayHoverPause) startAutoplay();
  };

  const renderChildren = useMemo(() => {
    let{items} = settings;
    let _children = React.Children.toArray(children);
    let _required = Math.max(2*items, totalItems+items);
    return new Array(_required).fill(undefined).map((_, index) => {
      return(<div className="cardslidercontainer__item" key={index}>
        {_children[index%totalItems]}
      </div>)
    })
  },[children,settings.items]);

  
  return (
    <div className="cardslidercontainer"
      ref={sliderRef}
      touch-action="none"
      onPointerEnter={() => {
        if (autoplayHoverPause) stopAutoplay();
      }}
      onPointerLeave={() => {
        if (autoplayHoverPause) startAutoplay();
      }}
      style={{ userSelect: "none" }}
    >
      <div
        className="cardslidercontainer__track"
        onPointerDown={handleDragStart}
        onPointerUp={handleDragEnd}
        ref={trackRef}
        style={{
          "--visible-items": settings.items,
          "--card-margin": `${settings.margin}px`,
          "--card-width": `calc((100% - ${(settings.items - 1) * settings.margin}px) / ${settings.items})`,
        }}
      >
        {renderChildren}
      </div>
      <button className="cardslidercontainer__button prev" onClick={handlePrev}><PreviousIcon/></button>
      <button className="cardslidercontainer__button next" onClick={handleNext}><PreviousIcon/></button>
      {!!dots && (<DotsComponent count={totalItems} activeIndex={currentIndex} onClick={handleGoTo}/>)}
    </div>
  );
});

export default Carousel;

